import { useCallback, useRef } from 'react';
import axios from 'axios';
import { useApi } from '@dreamtree-org/twreact-ui';
import apiMap from '@api';
import { getApiRoot } from './domainResolver';

/**
 * baseURL comes from the domain config loaded at startup (loadDomainConfig in main.jsx) —
 * '' means same-origin, which is every deployment today; a cross-origin one can point the
 * SPA elsewhere without a rebuild. withCredentials so the httpOnly JWT cookie is sent.
 *
 * Module scope, not an inline arrow: useApi calls this per request and ignores its
 * identity entirely, so rebuilding it every render bought nothing and implied a
 * memoization contract that does not exist.
 */
const makeRequestInstance = () => axios.create({ baseURL: getApiRoot(), withCredentials: true });

/**
 * App-configured wrapper around twreact-ui's useApi.
 * Returns { data, error, loading, fetchApi, fetchData }.
 *
 * The wrapping exists to fix a real defect in the library, not for ergonomics.
 * twreact-ui's `useApi` (dist/index.esm.js, hook body ~line 26573):
 *
 *   1. builds `fetchApi`/`fetchData` as plain function expressions on EVERY render —
 *      there is no useCallback/useMemo/useRef anywhere in the hook; and
 *   2. writes three state slots on every call — setLoading(true), setData(response.data)
 *      (always a fresh object, so React never bails out of the re-render), setLoading(false).
 *
 * Together those make `useCallback(..., [fetchApi])` feeding a `useEffect` an infinite
 * request loop by construction: request → state write → re-render → new fetchApi → new
 * callback → effect refires. That flooded /videos and /scraper until the browser ran out
 * of sockets (ERR_INSUFFICIENT_RESOURCES).
 *
 * Pinning the latest value in a ref and handing out permanently-stable wrappers is what
 * makes the `[fetchApi]` dependency arrays throughout this app correct. No lint rule
 * catches the original bug — exhaustive-deps considers those arrays right, because the
 * problem is an unstable value rather than a missing dependency. client/src/lib/api.test.js
 * is the guard instead; if you change this function, that test is the one that matters.
 */
export function useAppApi() {
  const api = useApi({ apiMap, createRequestInstance: makeRequestInstance });

  // Assigned during render rather than in an effect on purpose: an effect here would run
  // AFTER the consuming component's own data-loading effect on first mount, so the very
  // first call would go through a stale closure. Nothing invokes these during render, so
  // by the time either is called the ref already holds the committed-latest value.
  const ref = useRef(api);
  ref.current = api;

  const fetchApi = useCallback((...args) => ref.current.fetchApi(...args), []);
  const fetchData = useCallback((...args) => ref.current.fetchData(...args), []);

  return { ...api, fetchApi, fetchData };
}
